<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import FileUpload from 'primevue/fileupload';
import FilePreview from './components/FilePreview.vue';
import Dialog from 'primevue/dialog';
import useFileList from '@/composables/useFileList';
import { EMIT } from '@/enum';
import { FileInterface } from '@/types';
import { useTenantStore } from '@/store';

interface PropsInterface {
  title: string;
  loading?: boolean;
}

const emit = defineEmits<{
  (eventName: EMIT.FILE_DROPPED, files: string[]): void;
  (eventName: EMIT.SAVE): void;
  (eventName: EMIT.CANCEL): void;
  (eventName: EMIT.ERROR): void;
}>();

const props = defineProps<PropsInterface>();
const tenantStore = useTenantStore();
const { title } = toRefs(props);
const { files, addFiles, removeFile } = useFileList();
const modalVisible = ref(true);

const hasError = computed(() => !!tenantStore.error);

/**
 * create the form data for proper POST
 * of blob documents
 */
const createForm = (file: FileInterface) => {
  const formData = new FormData();
  formData.append('file', file.file as Blob);

  return formData;
};

/**
 * emit the SAVE to the parent with the files to upload,
 * reset the array and hide the modal
 * note: this only handles ONE file upload
 */
const save = async () => {
  const file: FileInterface = [...files.value][0];
  const formData = createForm(file);
  await tenantStore.uploadRSMFile(formData);

  if (!hasError.value) {
    emit(EMIT.SAVE);
  } else {
    removeFile(file);
    emit(EMIT.ERROR);
  }
  modalVisible.value = false;
};

/**
 * on dropping the file to the uploader add to file storage
 */
const onDrop = (e: Event) => addFiles(e.files);

/**
 * get the form items and make sure theyre completed
 * before enabling the save button
 * @returns boolean
 */
const formDisabled = computed<boolean>(() => ![...files.value].length);

const cancel = () => {
  removeError();
  emit(EMIT.CANCEL);
  modalVisible.value = false;
};

const removeError = () => (tenantStore.error = undefined);
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    class="dialog__container add-document__modal"
    :modal="true"
    :draggable="false"
    @hide="cancel"
  >
    <template #header>
      <div class="dialog__header flex flex-column">
        <h2 class="m-0" v-text="props.title" />
      </div>
    </template>
    <div class="upload-wrapper flex relative text-center align-items-center justify-content-center w-full">
      <FileUpload
        v-if="!files.length"
        :custom-upload="true"
        class="upload relative"
        choose-label="Choose file"
        accept=".csv,"
        :invalid-file-type-message="'Sorry, this file type is not supported. Please select a supported file type: {1}'"
        :auto="true"
        mode="advanced"
        :show-cancel-button="false"
        :show-upload-button="false"
        :max-file-size="10000000"
        invalid-file-size-message="Error: This file exceeds the max file size allowed."
        @uploader="onDrop"
      >
        <template #empty>
          <p class="uploader-text font-bold text-base mt-2">
            <span class="underline cursor-pointer">Choose File</span>
            or drag and drop here
          </p>
          <p class="acceptable-files-text">Allowed types: .csv</p>
          <p class="acceptable-files-text">Max File size: 10MB</p>
        </template>
      </FileUpload>
      <ul v-else class="upload-wrapper__file-list flex w-full h-full list-none absolute left-0 align-items-center pl-3">
        <FilePreview
          v-for="file of files"
          :key="file.id"
          class="w-full flex text-right align-items-center"
          :file="file"
          tag="li"
          @remove="removeFile"
        />
      </ul>
    </div>
    <template #footer>
      <AppButton type="secondary" text="Cancel" @click="cancel" />
      <AppButton type="primary" :text="title" :disabled="formDisabled" @click="save" />
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.dialog__container {
  .p-dialog-content {
    padding-top: 0;

    .upload-wrapper {
      height: 98px;
      padding-top: 0;
      margin-top: 0;
      align-items: flex-start;

      :deep(.p-fileupload-buttonbar) {
        padding-top: 0;
        background: none;
        border: none;

        .p-button {
          margin-left: -77%;
          padding: 6px;
          font-weight: bold;
          cursor: pointer;
          z-index: 5;
          border: none;

          .p-button-label {
            text-decoration: underline;
            font-size: 16px;
          }
          .p-button-icon {
            display: none;
          }
        }
      }
      :deep(.p-fileupload-content) {
        text-align: center;
        border: 2px dashed $grayMedium;
        border-radius: 8px;
        height: 98px;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        background: transparent;
        display: flex;
        justify-content: center;

        .p-message-error {
          position: absolute;
          top: -51px;
          width: 100%;
        }
        .p-fileupload-empty {
          position: relative;
          top: -10px;
          .uploader-text {
            color: var(--primary-color-dark);
            line-height: 18px;
            margin-bottom: 4px;
          }
          .acceptable-files-text {
            font-size: 10px;
            line-height: 12px;
            color: var(--primary-color-dark);
          }
        }
      }
      :deep(.p-fileupload-highlight) {
        border-color: var(--primary-color);
      }

      .upload-wrapper__file-list {
        color: $grayDark;
        font-size: 14px;
        line-height: 18px;
        border: 2px dashed $grayMedium;
        border-radius: 8px;
        height: 98px;
        padding: 1rem 2rem;
        top: -14px;
      }

      .v-enter-active,
      .v-leave-active {
        transition: opacity 0.5s ease;
      }
      .v-enter-from,
      .v-leave-to {
        opacity: 0;
      }
      .upload-wrapper__error {
        color: #822508;
        top: -65px;
        height: 37px;
        z-index: 6;
      }
    }

    .description-field {
      margin-top: 6px;
      margin-bottom: 6px;

      .description-field__input {
        margin: 2px 0;
      }
    }
  }
}
</style>
